import React, { useState, useContext } from 'react'
import { Drawer } from '@mui/material'
import { Grant } from '../../../../generated/graphql'
import {
  formatNumber,
  getCurrency,
  getSymbolOfCurrency,
} from '../../../../utils/CurrencyFormatter'
import { GrantCalculatorDrawer } from './GrantCalculatorDrawer'
import { KTSVG } from '../../../../_metronic/helpers'
import { InfoTooltip } from '../../../../utils/Utils'
import { isoToDate } from '../../../../utils/FormatDate'
import { GlobalContext } from '../../store/context/globalContext'

interface GrantsRequiringActionDrawerProps {
  isOpen: boolean
  onClose: () => void
  grants: Grant[]
  onAddFMVClick?: (grant: Grant) => void
  expiringWindow: string
}

export const GrantsRequiringActionDrawer: React.FC<GrantsRequiringActionDrawerProps> = ({
  isOpen,
  onClose,
  grants,
  onAddFMVClick,
  expiringWindow,
}) => {
  const { state } = useContext(GlobalContext)
  const { userCurrency } = state
  const userCurrencyFormat = getCurrency(userCurrency)
  
  const [calculatorDrawerOpen, setCalculatorDrawerOpen] = useState(false)
  const [selectedGrant, setSelectedGrant] = useState<Grant | null>(null)

  const filteredGrants = grants.filter((grant) => {
    if (!grant.expiryDate) return false
    const expiry = new Date(grant.expiryDate)
    const daysUntilExpiry = (expiry.getTime() - Date.now()) / (1000 * 60 * 60 * 24)
    return daysUntilExpiry >= 0
  })

  const getMonthsUntilExpiry = (expiryDate: string) => {
    const expiry = new Date(expiryDate)
    const now = new Date()
    const diffInMonths =
      (expiry.getFullYear() - now.getFullYear()) * 12 + expiry.getMonth() - now.getMonth()
    return Math.max(0, Math.round(diffInMonths))
  }

  const getDaysUntilExpiry = (expiryDate: string) => {
    const expiry = new Date(expiryDate)
    const now = new Date()
    const diffInMs = expiry.getTime() - now.getTime()
    return Math.ceil(diffInMs / (1000 * 60 * 60 * 24))
  }

  const handleCalculateExerciseCost = (grant: Grant) => {
    setSelectedGrant(grant)
    setCalculatorDrawerOpen(true)
  }

  const formatCurrency = (value: number): string => {
    return `${getSymbolOfCurrency(userCurrency)}${formatNumber({
      value: Number(value),
      format: userCurrencyFormat,
    })}`
  }

  const minDaysUntilExpiry = filteredGrants.length
    ? Math.min(...filteredGrants.map((grant) => getDaysUntilExpiry(grant.expiryDate!)))
    : null

  return (
    <Drawer anchor='right' open={isOpen} onClose={onClose}>
      <div className='p-6' style={{ width: '650px', maxWidth: '100vw' }}>
        {/* Header Section */}
        <div className='header-section'>
          <div className='d-flex justify-content-between align-items-center mb-3'>
            <h5 className='fw-bold mb-0'>Grants Requiring Action</h5>
            <button className='btn btn-sm btn-icon' onClick={onClose}>
              <i className='bi bi-x-lg' />
            </button>
          </div>

          <div className='text-gray-700 mb-4'>
            {filteredGrants.length} grant{filteredGrants.length !== 1 ? 's' : ''} expiring in the
            next <strong>{expiringWindow}</strong>
          </div>
        </div>

        {/* Grants List Section */}
        <div className='grants-list-section'>
          {filteredGrants.map((grant, idx) => {
            const vestedOptions = grant.vestedOptions ?? 0
            const numberExercised = grant.numberExercised ?? 0
            const toExercise = vestedOptions - numberExercised

            // const exercisePrice = grant.exercisePriceOrSarValue ?? 0
            const exercisePrice = Number(grant.strikePriceinUserCurrency) ?? 0

            // const companyFMV = grant.companyFMV ?? null
            const companyFMV = Number(grant.companyFmvinUserCurrency) ?? null

            const exerciseCost = vestedOptions * exercisePrice
            const currentValue = companyFMV !== null ? vestedOptions * companyFMV : null
            const netGain = currentValue !== null ? currentValue - exerciseCost : null
            const expiryDate = grant.expiryDate ? new Date(grant.expiryDate) : null
            const monthsUntilExpiry = expiryDate ? getMonthsUntilExpiry(grant.expiryDate) : 0
            const daysUntilExpiry = expiryDate ? getDaysUntilExpiry(grant.expiryDate) : 0

            return (
              <div
                key={grant.grantId || idx}
                className='grant-card border rounded bg-white p-6 mb-4'
                style={{ borderLeft: '4px solid #0d6efd' }}
              >
                {/* Grant Header */}
                <div className='grant-header mb-3'>
                  <div className='d-flex justify-content-between align-items-center mb-1 flex-wrap gap-2'>
                    {/* Left: Company + Expiry Badge */}
                    <div className='company-info d-flex align-items-center flex-wrap'>
                      <h6 className='fw-bold mb-0 me-2'>{grant.companyName || '—'}</h6>
                      {monthsUntilExpiry !== null && monthsUntilExpiry !== undefined && (
                        <span
                          className='badge text-white px-3 py-2 rounded-pill fw-semibold'
                          style={{ background: '#f59f0a', fontSize: '0.75rem' }}
                        >
                          expiring in {monthsUntilExpiry} month{monthsUntilExpiry !== 1 ? 's' : ''}
                        </span>
                      )}
                    </div>

                    {/* Right: Net Gain */}
                    <div className='net-gain-info text-end'>
                      <div className='fw-semibold text-success' style={{ fontSize: '1rem' }}>
                        {netGain !== null ? formatCurrency(netGain) : 'N/A'}
                      </div>
                    </div>
                  </div>

                  {/* Vested/Expiry + Net Gain info in single line */}
                  <div className='d-flex justify-content-between align-items-center text-gray-700 small'>
                    <div className='grant-details'>
                      {toExercise.toLocaleString(userCurrencyFormat)} options vesting • Expires{' '}
                      {expiryDate
                        ? isoToDate(expiryDate.toISOString().split('T')[0] ?? '')
                        : 'No expiry'}
                    </div>
                    <div className='text-end'>
                      <div className='text-gray-700 fs-7'>Net gain if exercised</div>
                    </div>
                  </div>
                </div>

                {/* Financial Details Grid */}
                <div className='financial-details-section mb-3'>
                  <div className='row g-3'>
                    <div className='col-12 col-md-6 mb-md-0 mb-3'>
                      <div className='bg-light rounded p-4'>
                        <div className='text-gray-600 fs-5'>Exercise Cost</div>
                        <div className='fw-semibold'>{formatCurrency(exerciseCost)}</div>
                      </div>
                    </div>
                    <div className='col-12 col-md-6 mb-md-0 mb-3'>
                      <div className='bg-light rounded p-4'>
                        <div className='text-gray-600 fs-5'>Current Value</div>
                        <div className='fw-semibold'>
                          {currentValue !== null ? formatCurrency(currentValue) : 'N/A'}
                        </div>
                      </div>
                    </div>
                    <div className='col-12 col-md-6 mb-md-0 mb-3'>
                      <div className='bg-light rounded p-4'>
                        <div className='text-gray-600 fs-6'>FMV per Share</div>
                        <div className='fw-semibold'>
                          {companyFMV !== null ? formatCurrency(companyFMV) : 'N/A'}
                        </div>
                      </div>
                    </div>
                    <div className='col-12 col-md-6 mb-md-0 mb-3'>
                      <div className='bg-light rounded p-4'>
                        <div className='text-gray-600 fs-6'>Strike Price</div>
                        <div className='fw-semibold'>{formatCurrency(exercisePrice)}</div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Expiry Information */}
                <div className='expiry-section mb-3'>
                  <div className='d-flex justify-content-between align-items-center p-2 bg-light rounded'>
                    <div className='expiry-label text-gray-700 d-flex align-items-center gap-2'>
                      <KTSVG
                        path='/media/icons/duotune/general/gen014.svg'
                        className='svg-icon svg-icon-2'
                      />
                      Expires
                    </div>
                    <div className='expiry-details'>
                      <div className='fw-semibold text-end fs-6 mb-1'>
                        {expiryDate ? expiryDate.toISOString().split('T')[0] : 'No expiry'}
                      </div>
                      <span
                        className='badge text-white px-2 py-2 rounded-pill'
                        style={{ background: '#f59f0a', fontSize: '0.75rem' }}
                      >
                        {daysUntilExpiry} day{daysUntilExpiry !== 1 ? 's' : ''} remaining
                      </span>
                    </div>
                  </div>
                </div>
                {/* Action Recommendation */}
                <div className='action-section'>
                  <div
                    className='bg-warning-subtle border border-warning rounded p-3 p-md-4 d-flex flex-column flex-md-row align-items-center align-items-md-center flex-nowrap overflow-hidden'
                    style={{ backgroundColor: '#fefaf3' }}
                  >
                    {/* Info Section */}
                    <div
                      className='action-info d-flex align-items-start align-items-md-center me-0 me-md-3 flex-grow-1 mb-3 mb-md-0'
                      style={{ maxWidth: '100%' }}
                    >
                      <i className='bi bi-exclamation-triangle text-warning me-2 fs-3 flex-shrink-0' />
                      <div className='small'>
                        <div className='fw-semibold text-dark fs-6'>Action Recommended</div>
                        <div className='action-description text-gray-600 fs-7'>
                          Exercising {vestedOptions.toLocaleString(userCurrencyFormat)} options will cost{' '}
                          {formatCurrency(exerciseCost)} but unlock{' '}
                          {currentValue !== null
                            ? formatCurrency(currentValue)
                            : 'value cannot be calculated without FMV'}{' '}
                          in value
                        </div>
                      </div>
                    </div>

                    {/* Button Section */}
                    <button
                      className='btn btn-primary btn-sm fs-7 d-flex align-items-center justify-content-center'
                      style={{ maxWidth: '160px', whiteSpace: 'normal', flexShrink: 0 }}
                      onClick={() => handleCalculateExerciseCost(grant)}
                    >
                      <KTSVG
                        path='/media/icons/duotune/finance/fin007.svg'
                        className='svg-icon-2 svg-icon fs-10 me-2'
                      />
                      Calculate Exercise Cost
                    </button>
                  </div>
                </div>
              </div>
            )
          })}
        </div>

        {/* Calculator Drawer */}
        {selectedGrant && (
          <div className='calculator-section'>
            <GrantCalculatorDrawer
              isOpen={calculatorDrawerOpen}
              onClose={() => {
                setCalculatorDrawerOpen(false)
                setSelectedGrant(null)
              }}
              grant={selectedGrant}
            />
          </div>
        )}
      </div>
    </Drawer>
  )
}